import { isNative } from '@dhl/webview'

export const oneTrustConsentChangedEvent = 'onetrust-consent-changed'

export const consentGroups = {
  essential: 'C0001',
  performance: 'C0002',
  functional: 'C0003',
  analytics: 'C0004',
}

const getCookieValueByName = (name: string) =>
  document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split(/=(.+)/))
    .find(([key]) => key === name)?.[1]

const getOptanonConsentValues = () =>
  getCookieValueByName('OptanonConsent')
    ?.split('&')
    .reduce<Record<string, string>>((acc, cur) => {
      const [key, value] = cur.split('=')
      acc[key] = decodeURIComponent(value)
      return acc
    }, {}) ?? {}

export const getConsentedGroups = (): string[] => {
  const { groups } = getOptanonConsentValues()

  return (
    groups
      ?.split(',')
      .map((group) => group.split(':'))
      .filter(([, value]) => value === '1')
      .map(([key]) => key) || []
  )
}

const createDarkModeStyle = (nonce: string, isEnabled?: boolean) => {
  const styleElement = document.getElementById('onetrustDarkModeStyle')

  if ((styleElement && isEnabled) || (!styleElement && !isEnabled)) {
    return
  }

  if (styleElement && !isEnabled) {
    styleElement.remove()
    return
  }

  const style = document.createElement('style')
  style.id = 'onetrustDarkModeStyle'
  style.nonce = nonce

  style.innerHTML = `
      #onetrust-consent-sdk #onetrust-pc-sdk h3, 
      #onetrust-consent-sdk #onetrust-pc-sdk h4, 
      #onetrust-consent-sdk #onetrust-pc-sdk h5, 
      #onetrust-consent-sdk #onetrust-pc-sdk h6, 
      #onetrust-consent-sdk #onetrust-pc-sdk p, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-ven-lst .ot-ven-opts p, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-pc-desc, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-pc-title, 
      #onetrust-consent-sdk #onetrust-pc-sdk .ot-li-title, 
      #onetrust-consent-sdk #onetrust-pc-sdk .ot-sel-all-hdr span, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-host-lst .ot-host-info, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-fltr-modal #modal-header, 
      #onetrust-consent-sdk #onetrust-pc-sdk .ot-checkbox label span, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-pc-lst #ot-sel-blk p, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-pc-lst #ot-lst-title h3, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-pc-lst .back-btn-handler p, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-pc-lst .ot-ven-name, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-pc-lst #ot-ven-lst .consent-category, 
      #onetrust-consent-sdk #onetrust-pc-sdk .ot-leg-btn-container .ot-inactive-leg-btn, 
      #onetrust-consent-sdk #onetrust-pc-sdk .ot-label-status, 
      #onetrust-consent-sdk #onetrust-pc-sdk .ot-chkbox label span, 
      #onetrust-consent-sdk #onetrust-pc-sdk #clear-filters-handler, 
      #onetrust-consent-sdk #onetrust-pc-sdk .ot-optout-signal {
        color: #fff
      }

      #onetrust-consent-sdk #onetrust-pc-sdk, 
      #onetrust-consent-sdk #ot-search-cntr, 
      #onetrust-consent-sdk #onetrust-pc-sdk .ot-switch.ot-toggle, 
      #onetrust-consent-sdk #onetrust-pc-sdk ot-grp-hdr1 .checkbox, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-pc-title:after, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-sel-blk, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-fltr-cnt, 
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-anchor, 
      #onetrust-consent-sdk #onetrust-pc-sdk .ot-acc-grpcntr.ot-acc-txt,
      #onetrust-consent-sdk #onetrust-pc-sdk #ot-host-lst .ot-host-info, 
      #onetrust-consent-sdk #onetrust-pc-sdk .ot-acc-txt .ot-ven-dets {
        background-color: #262626
      }
  `

  document.head.appendChild(style)
}

export const shouldShowBanner = (): boolean => getCookieValueByName('OptanonAlertBoxClosed') === undefined

/**
 * See https://developer.onetrust.com/onetrust/docs/display-preference-center
 */
type Props = {
  enableDarkModeStyle?: boolean
  nonce: string
}
export const showPreferenceCentre = ({ enableDarkModeStyle = false, nonce }: Props): void => {
  if (!isNative()) {
    return
  }

  const styleElement = document.getElementById('onetrustPreferenceCentreStyle')

  const otConsentSdk = document.getElementById('onetrust-consent-sdk')
  const banner = document.getElementById('onetrust-banner-sdk')
  const prefpc = document.getElementById('onetrust-pc-sdk')

  if (banner) {
    banner.style.display = 'none'
  }

  if (otConsentSdk && prefpc) {
    otConsentSdk.style.display = 'block'
    prefpc.style.setProperty('display', 'block', 'important')
  }

  createDarkModeStyle(nonce, enableDarkModeStyle)

  if (styleElement) {
    return
  }

  const style = document.createElement('style')
  style.id = 'onetrustPreferenceCentreStyle'
  style.nonce = nonce

  style.innerHTML = `
    #onetrust-consent-sdk {
      display: block !important;
    }

    #onetrust-pc-sdk.otPcCenter {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      min-width: 0 !important;
      max-width: 100% !important;
      width: 100% !important;
      bottom: 0 !important;
      min-height: 100% !important;
    }

    #ot-pc-content {
      overflow: hidden !important; 
      padding: 16px !important; 
      margin: 0 !important; 
      width: calc(100% - 32px) !important; 
      height: calc(100% - 32px) !important; 
      bottom: 0 !important; 
      position: fixed !important; 
      top: 0 !important; 
      left: 0 !important;
    }

    #ot-pc-content .ot-sdk-row {
      height: calc(100% - 154px) !important; 
      overflow-y: auto !important; 
      -webkit-overflow-scrolling: touch !important;
    }

    #accept-recommended-btn-handler,
    .save-preference-btn-handler {
      position: fixed !important; 
      bottom: 88px !important; 
      left: 16px !important; 
      right: 16px !important; 
      margin: 0 !important; 
      width: auto !important; 
      background: #D40511 !important; 
      color: #fff !important; 
      z-index: 9999 !important; 
      height: 48px !important; 
      border-radius: 4px !important;
      max-width: 100% !important;
      letter-spacing: normal !important;
      font-family: "Delivery Bold", Calibri, Arial, sans-serif !important;
      font-weight: normal !important;
    }

    .save-preference-btn-handler {
      bottom: 24px !important; 
    }

    #ot-pc-lst {
      top: 0 !important; 
      overflow-y: auto !important;
    }

    #ot-lst-cnt {
      padding: 0 !important; 
      margin: 0 !important; 
      width: 100% !important; 
      overflow-y: auto !important; 
      height: calc(100vh - 178px) !important; 
      max-height: inherit !important;
    }

    #ot-pc-title,
    #ot-pc-desc,
    .ot-lst-subhdr {
      display: none !important;
    }
  `

  document.head.appendChild(style)
}
